/**
 * Tool palette for the annotation editor: tool picker, colour swatches, stroke
 * width, undo/redo, Save/Cancel.
 *
 * Labels are resolved through `labelKey` + `t()` at render rather than stored as
 * literal strings in the tool table — `lint:i18n-const` specifically targets
 * `.map()` over an object array carrying a `label` property, and a literal there
 * would fail CI even though it renders correctly.
 */

import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowUpRight,
  Circle,
  MousePointer2,
  Pen,
  Redo2,
  Square,
  Trash2,
  Type,
  Undo2,
  X,
} from 'lucide-react'

import { cn } from '../../../../../lib/utils'
import { ANNOTATION_COLORS, ANNOTATION_TOOLS, type AnnotationTool } from './annotationTools'

const TOOL_ICONS: Record<AnnotationTool, React.ReactNode> = {
  select: <MousePointer2 className="w-4 h-4" strokeWidth={2} />,
  pen: <Pen className="w-4 h-4" strokeWidth={2} />,
  rect: <Square className="w-4 h-4" strokeWidth={2} />,
  ellipse: <Circle className="w-4 h-4" strokeWidth={2} />,
  arrow: <ArrowUpRight className="w-4 h-4" strokeWidth={2} />,
  text: <Type className="w-4 h-4" strokeWidth={2} />,
}

export interface AnnotationToolPaletteProps {
  tool: AnnotationTool
  onToolChange: (tool: AnnotationTool) => void
  color: string
  onColorChange: (color: string) => void
  width: number
  widthMin: number
  widthMax: number
  widthStep: number
  onWidthChange: (width: number) => void
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
  canDelete: boolean
  onDelete: () => void
  canSave: boolean
  isSaving: boolean
  onSave: () => void
  onCancel: () => void
  /**
   * Hide Save / Cancel. The inline Refine surface has no commit of its own —
   * the panel's Generate button is the action, and it bakes whatever is on the
   * canvas at that moment. Showing a second, differently-scoped Save there
   * would read as "save the drawing" and invite the user to press it first.
   */
  showCommitActions?: boolean
  /**
   * Render as a static block above the image instead of an overlay floating on
   * top of it. The Refine frames place the tool rows ABOVE the picture, in the
   * panel's right-hand column; the lightbox floats them over a viewport-sized
   * image where there is room to spare.
   */
  inline?: boolean
}

export const AnnotationToolPalette = memo(function AnnotationToolPalette({
  tool,
  onToolChange,
  color,
  onColorChange,
  width,
  widthMin,
  widthMax,
  widthStep,
  onWidthChange,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  canDelete,
  onDelete,
  canSave,
  isSaving,
  onSave,
  onCancel,
  showCommitActions = true,
  inline = false,
}: AnnotationToolPaletteProps) {
  const { t } = useTranslation('canvas')

  return (
    // The lightbox owns viewport space, so its compact two-row palette can sit
    // above the contained image without inheriting node width or canvas zoom.
    // Inline it is a plain block in the column's flow instead.
    <div
      data-testid="annotation-tool-palette"
      className={
        inline
          ? 'nodrag nopan order-first flex w-full shrink-0 flex-col items-start gap-1'
          : 'nodrag nopan absolute left-1/2 top-3 z-20 flex w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-col items-center gap-1 rounded-lg border border-border bg-surface-2/95 px-2 py-1.5 shadow-2xl backdrop-blur-md'
      }
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
     <div className="flex w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-1">
      {/* Tools */}
      <div className="flex items-center gap-0.5">
        {ANNOTATION_TOOLS.map((entry) => (
          <button
            key={entry.tool}
            type="button"
            data-testid={`annotation-tool-${entry.tool}`}
            aria-label={t(entry.labelKey)}
            title={t(entry.labelKey)}
            aria-pressed={tool === entry.tool}
            onClick={() => onToolChange(entry.tool)}
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-md transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              tool === entry.tool
                ? 'bg-primary/90 text-primary-foreground'
                : 'text-text-muted hover:bg-surface-1 hover:text-text-primary',
            )}
          >
            {TOOL_ICONS[entry.tool]}
          </button>
        ))}
      </div>

      <div className="h-5 w-px bg-border-subtle" />

      {/* History + delete */}
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          data-testid="annotation-undo"
          aria-label={t('annotate.undo')}
          title={t('annotate.undo')}
          disabled={!canUndo}
          onClick={onUndo}
          className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-1 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Undo2 className="w-4 h-4" strokeWidth={2} />
        </button>
        <button
          type="button"
          data-testid="annotation-redo"
          aria-label={t('annotate.redo')}
          title={t('annotate.redo')}
          disabled={!canRedo}
          onClick={onRedo}
          className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-1 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Redo2 className="w-4 h-4" strokeWidth={2} />
        </button>
        <button
          type="button"
          data-testid="annotation-delete"
          aria-label={t('annotate.deleteElement')}
          title={t('annotate.deleteElement')}
          disabled={!canDelete}
          onClick={onDelete}
          className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Trash2 className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
     </div>

     <div className="flex w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-1">
      {/* Colour */}
      <div className="flex items-center gap-1" role="group" aria-label={t('annotate.color')}>
        {ANNOTATION_COLORS.map((swatch) => (
          <button
            key={swatch}
            type="button"
            data-testid={`annotation-color-${swatch.slice(1)}`}
            aria-label={t(`annotate.colors.${swatch.slice(1)}`)}
            title={t(`annotate.colors.${swatch.slice(1)}`)}
            aria-pressed={color === swatch}
            onClick={() => onColorChange(swatch)}
            className={cn(
              'h-5 w-5 rounded-full border transition-transform',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              color === swatch
                ? 'border-foreground scale-110'
                : 'border-border-subtle hover:scale-105',
            )}
            style={{ backgroundColor: swatch }}
          />
        ))}
      </div>

      <div className="h-5 w-px bg-border-subtle" />

      {/* Width controls stroke thickness for drawing tools and font size for text. */}
      <label className="flex items-center gap-1.5 text-xxs text-text-muted">
        <span>{t('annotate.strokeWidth')}</span>
        <input
          type="range"
          data-testid="annotation-width"
          aria-label={t('annotate.strokeWidth')}
          min={widthMin}
          max={widthMax}
          step={widthStep}
          value={width}
          onChange={(event) => onWidthChange(Number(event.target.value))}
          className="h-1 w-14 cursor-pointer accent-primary"
        />
      </label>

      {showCommitActions && <div className="h-5 w-px bg-border-subtle" />}

      {/* Commit */}
      {showCommitActions && <div className="flex items-center gap-1">
        <button
          type="button"
          data-testid="annotation-cancel"
          aria-label={t('annotate.cancel')}
          title={t('annotate.cancel')}
          onClick={onCancel}
          className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-surface-1 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <X className="w-4 h-4" strokeWidth={2} />
        </button>
        <button
          type="button"
          data-testid="annotation-save"
          aria-label={t('annotate.save')}
          disabled={!canSave || isSaving}
          onClick={onSave}
          className="rounded-md bg-primary/90 px-3 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isSaving ? t('annotate.saving') : t('annotate.save')}
        </button>
      </div>}
     </div>
    </div>
  )
})

AnnotationToolPalette.displayName = 'AnnotationToolPalette'
